// ============================================================================
// RAID REGISTER
//
// The session-wide view: every risk, assumption, issue, dependency and open
// question in one filterable list. This is the screen shared during a workshop
// wrap-up to assign owners, so it leads with what is unresolved and who owns
// it, and defaults to hiding closed items.
//
// A top-level sidebar view rather than a tab inside RequirementsPanel, because
// that panel's inner navigation is persona-scoped and RAID spans stories,
// NFRs, features and ADRs.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFeatures, getADRs } from '../../../services/workshop';
import {
  ShieldAlert, Plus, Search, Filter, Pencil, User, Link2, AlertOctagon, CheckCircle2, ArrowUpDown, ChevronDown,
} from 'lucide-react';
import { useRaidItems, useRaidMutations } from '../../../hooks/useRaidItems';
import { RaidItem, RaidStatus, RaidType, RaidLink, RaidOwnerOrg } from '../../../types/raid';
import {
  RAID_TYPES, RAID_TYPE_ORDER, RAID_STATUSES, statusLabel, statusChip, ragChip, LINK_TARGET_LABELS,
} from './raidConfig';
import { RaidEditModal } from './RaidEditModal';
import { RaidResolveInline } from './RaidResolveInline';
import { RaidAssignInline } from './RaidAssignInline';
import { LinkCandidate } from './RaidLinkPicker';
import { RaidItemInput } from '../../../services/raid';

type SortBy = 'urgency' | 'score' | 'newest' | 'type';
type QuickFilter = 'blocking' | 'unowned' | null;

export interface RaidPanelProps {
  sessionId: string;
  /** Stories and NFRs come from the caller, which already holds them. */
  stories?: { id: string; story?: string; persona?: string }[];
  nfrs?: { id: string; title: string; category?: string }[];
}

type StatusFilter = 'open' | 'all' | RaidStatus;

export function RaidPanel({ sessionId, stories = [], nfrs = [] }: Readonly<RaidPanelProps>) {
  const scope = useMemo(() => ({ sessionId }), [sessionId]);
  const { data, isLoading } = useRaidItems(scope);
  const mutations = useRaidMutations(scope);

  // Features and ADRs live in RequirementsPanel's local state, so the register
  // fetches its own rather than coupling the two panels together.
  const { data: features = [] } = useQuery({
    queryKey: ['features', sessionId],
    queryFn: () => getFeatures(sessionId),
    enabled: !!sessionId,
    staleTime: 60000,
  });
  const { data: adrs = [] } = useQuery({
    queryKey: ['adrs', sessionId],
    queryFn: () => getADRs(sessionId),
    enabled: !!sessionId,
    staleTime: 60000,
  });

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<RaidType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(null);
  const [sortBy, setSortBy] = useState<SortBy>('urgency');
  const [resolving, setResolving] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [modal, setModal] = useState<
    { mode: 'edit'; item: RaidItem } | { mode: 'create'; links: RaidLink[] } | null
  >(null);

  const items = useMemo(() => data?.raidItems ?? [], [data]);

  const candidates = useMemo<LinkCandidate[]>(() => [
    ...stories.map(s => ({
      targetType: 'user_story' as const, targetId: s.id, targetSessionId: sessionId,
      targetPersona: s.persona, label: s.story ?? s.id, sublabel: s.persona,
    })),
    ...nfrs.map(n => ({
      targetType: 'nfr' as const, targetId: n.id, targetSessionId: sessionId,
      label: n.title, sublabel: n.category,
    })),
    ...features.map(f => ({
      targetType: 'feature' as const, targetId: f.id, targetSessionId: sessionId, label: f.name,
    })),
    ...adrs.map(a => ({
      targetType: 'adr' as const, targetId: a.id, targetSessionId: sessionId, label: a.title, sublabel: a.status,
    })),
    ...items.map(r => ({
      targetType: 'raid_item' as const, targetId: r.raidId, label: r.title, sublabel: RAID_TYPES[r.type].label,
    })),
  ], [sessionId, stories, nfrs, features, adrs, items]);

  const owners = useMemo(
    () => Array.from(new Set(items.map(i => i.owner).filter(Boolean))).sort() as string[],
    [items]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter(i => typeFilter === 'all' || i.type === typeFilter)
      .filter(i => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'open') return i.status === 'open';
        return i.status === statusFilter;
      })
      .filter(i => ownerFilter === 'all' || i.owner === ownerFilter)
      .filter(i => quickFilter !== 'blocking' || (i.status === 'open' && i.blockingSeverity === 'blocks'))
      .filter(i => quickFilter !== 'unowned' || (i.status === 'open' && !i.owner))
      .filter(i => !q
        || i.title.toLowerCase().includes(q)
        || (i.description ?? '').toLowerCase().includes(q)
        || (i.owner ?? '').toLowerCase().includes(q))
      .sort(SORTERS[sortBy]);
  }, [items, typeFilter, statusFilter, ownerFilter, quickFilter, sortBy, query]);

  const openCount = items.filter(i => i.status === 'open').length;
  const blockingCount = items.filter(i => i.status === 'open' && i.blockingSeverity === 'blocks').length;
  const unowned = items.filter(i => i.status === 'open' && !i.owner).length;

  const toggleQuickFilter = (next: QuickFilter) => setQuickFilter(prev => (prev === next ? null : next));
  const clearAllFilters = () => {
    setStatusFilter('all');
    setQuickFilter(null);
  };

  const handleSave = async (input: RaidItemInput) => {
    if (modal?.mode === 'edit') {
      await mutations.update.mutateAsync({ raidId: modal.item.raidId, patch: input });
    } else {
      await mutations.create.mutateAsync(input);
    }
    setModal(null);
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <ShieldAlert size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">RAID Register</h1>
              <p className="text-slate-500 text-sm font-medium">
                Risks, assumptions, issues, dependencies and open questions raised in this session
              </p>
            </div>
          </div>
          <button
            onClick={() => setModal({ mode: 'create', links: [] })}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Plus size={16} /> Raise item
          </button>
        </header>

        {/* Roll-up. Unowned is called out because the point of the wrap-up is
            leaving with a name against every open item. Each card doubles as
            a quick filter so the counts are never just decorative. */}
        <div className="flex flex-wrap gap-3">
          <Stat
            label="Open"
            value={openCount}
            active={statusFilter === 'open' && !quickFilter}
            onClick={() => { setStatusFilter('open'); setQuickFilter(null); }}
          />
          <Stat
            label="Blocking"
            value={blockingCount}
            tone={blockingCount > 0 ? 'red' : undefined}
            active={quickFilter === 'blocking'}
            onClick={() => toggleQuickFilter('blocking')}
          />
          <Stat
            label="Unowned"
            value={unowned}
            tone={unowned > 0 ? 'amber' : undefined}
            active={quickFilter === 'unowned'}
            onClick={() => toggleQuickFilter('unowned')}
          />
          <Stat
            label="Total"
            value={items.length}
            active={statusFilter === 'all' && !quickFilter}
            onClick={clearAllFilters}
          />
        </div>

        {/* Filters + list share one container */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0] flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2 border border-[#e2e8f0] rounded-xl px-3 py-2 flex-1 min-w-[220px] focus-within:ring-2 focus-within:ring-primary">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search title, detail or owner"
                className="flex-1 text-sm text-navy placeholder:text-slate-400 outline-none bg-transparent"
              />
            </div>

            <FilterSelect
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as RaidType | 'all')}
              aria-label="Filter by type"
            >
              <option value="all">All types</option>
              {RAID_TYPE_ORDER.map(t => <option key={t} value={t}>{RAID_TYPES[t].plural}</option>)}
            </FilterSelect>

            <FilterSelect
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              aria-label="Filter by status"
            >
              <option value="open">Open only</option>
              <option value="all">All statuses</option>
              {RAID_STATUSES.filter(s => s !== 'open').map(s => (
                // Wording varies by type, so the filter uses the neutral form.
                <option key={s} value={s}>{neutralStatusLabel(s)}</option>
              ))}
            </FilterSelect>

            <FilterSelect
              value={ownerFilter}
              onChange={e => setOwnerFilter(e.target.value)}
              aria-label="Filter by owner"
            >
              <option value="all">All owners</option>
              {owners.map(o => <option key={o} value={o}>{o}</option>)}
            </FilterSelect>

            <div className="flex items-center gap-2">
              <ArrowUpDown size={14} className="text-slate-400 shrink-0" />
              <FilterSelect
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortBy)}
                aria-label="Sort by"
              >
                <option value="urgency">Sort: Urgency</option>
                <option value="score">Sort: Score</option>
                <option value="newest">Sort: Newest raised</option>
                <option value="type">Sort: Type</option>
              </FilterSelect>
            </div>

            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Filter size={12} />{filtered.length} shown
            </span>
          </div>

          {isLoading && (
            <div className="flex justify-center items-center py-20 text-[#64748b] text-sm">Loading register…</div>
          )}

          {!isLoading && filtered.length === 0 && (
            <div className="rounded-xl bg-white p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <ShieldAlert size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                {items.length === 0 ? 'Nothing raised yet.' : 'Nothing matches these filters.'}
              </p>
              <p className="text-slate-400 text-xs mt-2">
                {items.length === 0
                  ? 'Raise items here or from any story, NFR, feature or ADR.'
                  : 'Try widening the status or type filter.'}
              </p>
            </div>
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="divide-y divide-[#e2e8f0]">
              {filtered.map(item => (
                <RegisterRow
                  key={item.raidId}
                  item={item}
                  isResolving={resolving === item.raidId}
                  isAssigning={assigning === item.raidId}
                  saving={mutations.resolve.isPending}
                  assigningSaving={mutations.update.isPending}
                  onStartResolve={() => setResolving(prev => (prev === item.raidId ? null : item.raidId))}
                  onCancelResolve={() => setResolving(null)}
                  onResolve={(status, resolution) => {
                    mutations.resolve.mutate({ raidId: item.raidId, status, resolution });
                    setResolving(null);
                  }}
                  onStartAssign={() => setAssigning(prev => (prev === item.raidId ? null : item.raidId))}
                  onCancelAssign={() => setAssigning(null)}
                  onAssign={(owner, ownerOrg) => {
                    mutations.update.mutate({ raidId: item.raidId, patch: { owner, ownerOrg: ownerOrg || undefined } });
                    setAssigning(null);
                  }}
                  onEdit={() => setModal({ mode: 'edit', item })}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {modal && (
        <RaidEditModal
          item={modal.mode === 'edit' ? modal.item : null}
          defaults={modal.mode === 'create' ? { links: modal.links } : undefined}
          candidates={candidates}
          saving={mutations.create.isPending || mutations.update.isPending}
          onSave={handleSave}
          onResolve={modal.mode === 'edit'
            ? (status, resolution) => {
                mutations.resolve.mutate({ raidId: modal.item.raidId, status, resolution });
                setModal(null);
              }
            : undefined}
          onDelete={modal.mode === 'edit'
            ? async () => { await mutations.remove.mutateAsync(modal.item.raidId); setModal(null); }
            : undefined}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ============================================================================

/** Blocking first, then open, then by risk score. What needs attention rises. */
function byUrgency(a: RaidItem, b: RaidItem): number {
  const rank = (i: RaidItem) => {
    if (i.status === 'open' && i.blockingSeverity === 'blocks') return 0;
    if (i.status === 'open') return 1;
    return 2;
  };
  const diff = rank(a) - rank(b);
  if (diff !== 0) return diff;
  return (b.riskScore ?? 0) - (a.riskScore ?? 0);
}

function byScore(a: RaidItem, b: RaidItem): number {
  return (b.riskScore ?? 0) - (a.riskScore ?? 0);
}

function byNewest(a: RaidItem, b: RaidItem): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function byType(a: RaidItem, b: RaidItem): number {
  return RAID_TYPE_ORDER.indexOf(a.type) - RAID_TYPE_ORDER.indexOf(b.type);
}

const SORTERS: Record<SortBy, (a: RaidItem, b: RaidItem) => number> = {
  urgency: byUrgency,
  score: byScore,
  newest: byNewest,
  type: byType,
};

function neutralStatusLabel(status: RaidStatus): string {
  switch (status) {
    case 'resolved': return 'Resolved';
    case 'deferred': return 'Deferred';
    case 'invalidated': return 'Invalidated';
    default: return "Won't action";
  }
}

/**
 * Native <select> arrow sits inside the same box as the text with no
 * reserved space, so long option labels run under it. `appearance-none` +
 * pr-8 clears that space and a Lucide chevron replaces the native arrow.
 */
function FilterSelect({
  children, className, ...props
}: Readonly<React.SelectHTMLAttributes<HTMLSelectElement>>) {
  return (
    <div className="relative">
      <select
        {...props}
        className={`appearance-none border border-[#e2e8f0] rounded-xl pl-3 pr-8 py-2 text-sm text-navy bg-white outline-none focus:ring-2 focus:ring-primary ${className ?? ''}`}
      >
        {children}
      </select>
      <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}

function Stat({
  label, value, tone, active, onClick,
}: Readonly<{ label: string; value: number; tone?: 'red' | 'amber'; active?: boolean; onClick?: () => void }>) {
  let border = 'border-primary/30';
  let text = 'text-navy';
  if (tone === 'red') { border = 'border-red-300'; text = 'text-red-600'; }
  if (tone === 'amber') { border = 'border-amber-300'; text = 'text-amber-600'; }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex flex-col text-left bg-white border rounded-lg px-4 py-2 min-w-[120px] shadow-sm transition-colors ${border} ${
        active ? 'ring-2 ring-primary/40 bg-primary/5' : 'hover:bg-slate-50'
      }`}
    >
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      <span className={`text-2xl font-bold ${text}`}>{value}</span>
    </button>
  );
}

/** Longer than this and the description gets truncated with a show-more toggle. */
const DESCRIPTION_TRUNCATE_LENGTH = 140;

interface RegisterRowProps {
  item: RaidItem;
  isResolving: boolean;
  isAssigning: boolean;
  saving: boolean;
  assigningSaving: boolean;
  onStartResolve: () => void;
  onCancelResolve: () => void;
  onResolve: (status: RaidStatus, resolution: string) => void;
  onStartAssign: () => void;
  onCancelAssign: () => void;
  onAssign: (owner: string, ownerOrg: RaidOwnerOrg | '') => void;
  onEdit: () => void;
}

function RegisterRow({
  item, isResolving, isAssigning, saving, assigningSaving,
  onStartResolve, onCancelResolve, onResolve,
  onStartAssign, onCancelAssign, onAssign, onEdit,
}: Readonly<RegisterRowProps>) {
  const cfg = RAID_TYPES[item.type];
  const Icon = cfg.icon;
  const isOpen = item.status === 'open';
  const blocking = isOpen && item.blockingSeverity === 'blocks';
  const [descExpanded, setDescExpanded] = useState(false);
  const descTooLong = (item.description?.length ?? 0) > DESCRIPTION_TRUNCATE_LENGTH;

  return (
    <div className={`group px-5 py-4 ${blocking ? 'bg-red-50/40' : ''}`}>
      <div className="flex items-start gap-3">
        {/* Fixed-width column so every row's title starts at the same x
            regardless of whether the label is "Risk" or "Assumption". */}
        <div className="w-[104px] shrink-0 mt-0.5 flex justify-center">
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold ${cfg.chip}`}>
            <Icon size={10} />{cfg.label}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium leading-snug ${isOpen ? 'text-navy' : 'text-slate-400 line-through'}`}>
            {item.title}
          </p>
          {item.description && (
            <div className="mt-0.5">
              <p className={`text-xs text-slate-500 ${descTooLong && !descExpanded ? 'line-clamp-2' : ''}`}>
                {item.description}
              </p>
              {descTooLong && (
                <button
                  type="button"
                  onClick={() => setDescExpanded(v => !v)}
                  className="text-[11px] font-semibold text-primary hover:text-primary-dark transition-colors mt-0.5"
                >
                  {descExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${statusChip(item.status)}`}>
              {statusLabel(item.type, item.status)}
            </span>
            {blocking && (
              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-600 text-[10px] font-semibold">
                <AlertOctagon size={9} />Blocks
              </span>
            )}
            {item.type === 'risk' && item.riskScore != null && (
              <span
                className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${ragChip(item.rag)}`}
                title="Probability × Impact (1-5 each). Red ≥15, amber 8-14, green ≤6."
              >
                Score {item.riskScore}
              </span>
            )}
            {item.owner ? (
              <button
                type="button"
                onClick={onStartAssign}
                className="flex items-center gap-0.5 text-[11px] text-slate-500 hover:text-primary transition-colors"
                title="Reassign owner"
              >
                <User size={10} />{item.owner}
                {item.ownerOrg && <span className="text-slate-300"> · {item.ownerOrg === 'client' ? 'Client' : 'Version 1'}</span>}
              </button>
            ) : isOpen && (
              <button
                type="button"
                onClick={onStartAssign}
                className="text-[11px] text-amber-600 font-semibold hover:text-amber-700 transition-colors underline decoration-dotted"
              >
                Unowned — assign
              </button>
            )}
            {item.dueDate && <span className="text-[11px] text-slate-400">due {item.dueDate}</span>}
            {(item.links?.length ?? 0) > 0 && (
              <span className="flex items-center gap-0.5 text-[11px] text-slate-400" title={linkSummary(item)}>
                <Link2 size={10} />{item.links!.length} linked
              </span>
            )}
          </div>

          {item.resolution && (
            <p className="flex items-start gap-1 text-[11px] text-slate-500 italic mt-1">
              <CheckCircle2 size={11} className="text-emerald-500 mt-0.5 shrink-0" />{item.resolution}
            </p>
          )}

          {isResolving && (
            <div className="mt-2">
              <RaidResolveInline
                compact
                type={item.type}
                currentStatus={item.status}
                currentResolution={item.resolution}
                saving={saving}
                onResolve={onResolve}
                onCancel={onCancelResolve}
              />
            </div>
          )}

          {isAssigning && (
            <div className="mt-2">
              <RaidAssignInline
                currentOwner={item.owner}
                currentOwnerOrg={item.ownerOrg ?? ''}
                saving={assigningSaving}
                onAssign={onAssign}
                onCancel={onCancelAssign}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isOpen && (
            <button
              onClick={onStartResolve}
              className="text-xs font-semibold text-primary hover:text-primary-dark transition-colors"
            >
              Resolve
            </button>
          )}
          <button
            onClick={onEdit}
            aria-label="Edit RAID item"
            className="p-1 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
          >
            <Pencil size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function linkSummary(item: RaidItem): string {
  return (item.links ?? [])
    .map(l => `${LINK_TARGET_LABELS[l.targetType] ?? l.targetType}: ${l.label ?? l.targetId}`)
    .join('\n');
}
